// Tiện ích media: đọc metadata video và trích xuất audio 16kHz mono cho Whisper.

/** Lấy thời lượng (giây) và kích thước khung hình của file video. */
export function probeVideo(url: string): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const v = document.createElement('video')
    v.preload = 'metadata'
    v.muted = true
    v.src = url
    v.onloadedmetadata = () => {
      resolve({
        duration: v.duration,
        width: v.videoWidth || 1280,
        height: v.videoHeight || 720,
      })
    }
    v.onerror = () => reject(new Error('Không đọc được video. Định dạng có thể không được trình duyệt hỗ trợ.'))
  })
}

const AudioCtx: typeof AudioContext =
  window.AudioContext ||
  (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext

/**
 * Trích xuất audio mono 16kHz cho Whisper.
 * Thử giải mã trực tiếp trước (nhanh); nếu thất bại (nhiều video MP4/Veo
 * không giải mã được bằng decodeAudioData) thì chuyển sang "chế độ tương thích":
 * phát video qua Web Audio và thu mẫu theo thời gian thực.
 */
export async function extractAudio16k(
  file: File,
  onStatus?: (s: string) => void,
): Promise<Float32Array> {
  try {
    return await decodePath(file)
  } catch {
    onStatus?.('Giải mã trực tiếp thất bại — chuyển chế độ tương thích…')
    return await realtimePath(file, onStatus)
  }
}

/** Đường nhanh: decodeAudioData + resample offline. */
async function decodePath(file: File): Promise<Float32Array> {
  const arrayBuffer = await file.arrayBuffer()
  const ctx = new AudioCtx()
  try {
    // decodeAudioData "detach" buffer nên truyền bản sao.
    const decoded = await ctx.decodeAudioData(arrayBuffer.slice(0))
    const mono = downmix(decoded)
    if (maxAbs(mono) < 1e-5) throw new Error('silent')
    return await resampleTo16k(mono, decoded.sampleRate)
  } finally {
    await ctx.close()
  }
}

/** Đường tương thích: phát video, thu mẫu qua ScriptProcessor (gain 0 = không phát tiếng). */
async function realtimePath(file: File, onStatus?: (s: string) => void): Promise<Float32Array> {
  const url = URL.createObjectURL(file)
  const video = document.createElement('video')
  video.src = url
  video.playsInline = true
  video.preload = 'auto'

  try {
    await new Promise<void>((res, rej) => {
      video.onloadedmetadata = () => res()
      video.onerror = () => rej(new Error('Không mở được video để trích xuất âm thanh.'))
    })

    const ctx = new AudioCtx()
    await ctx.resume().catch(() => {})
    const source = ctx.createMediaElementSource(video)
    const gain = ctx.createGain()
    gain.gain.value = 0 // không nghe thấy tiếng trong lúc thu
    const processor = ctx.createScriptProcessor(4096, 1, 1)
    const chunks: Float32Array[] = []
    processor.onaudioprocess = (e) => {
      chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)))
    }
    source.connect(processor)
    processor.connect(gain)
    gain.connect(ctx.destination)

    try {
      await video.play()
    } catch {
      await ctx.close()
      throw new Error('Trình duyệt chặn phát âm thanh. Hãy bấm “Phiên âm (AI)” thêm một lần nữa.')
    }

    await new Promise<void>((resolve) => {
      const check = setInterval(() => {
        const d = video.duration || 0
        if (d) onStatus?.(`Trích xuất âm thanh… ${Math.round((video.currentTime / d) * 100)}%`)
        if (video.ended || (d && video.currentTime >= d - 0.05)) {
          clearInterval(check)
          resolve()
        }
      }, 400)
      video.onended = () => {
        clearInterval(check)
        resolve()
      }
    })

    const sampleRate = ctx.sampleRate
    processor.disconnect()
    source.disconnect()
    gain.disconnect()
    await ctx.close()

    const merged = concatFloat32(chunks)
    if (merged.length === 0 || maxAbs(merged) < 1e-5) {
      throw new Error('Video này không có âm thanh để phiên âm.')
    }
    return await resampleTo16k(merged, sampleRate)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Trộn nhiều kênh về mono. */
function downmix(buffer: AudioBuffer): Float32Array {
  const ch = buffer.numberOfChannels
  if (ch === 1) return buffer.getChannelData(0)
  const len = buffer.length
  const out = new Float32Array(len)
  for (let c = 0; c < ch; c++) {
    const data = buffer.getChannelData(c)
    for (let i = 0; i < len; i++) out[i] += data[i] / ch
  }
  return out
}

function concatFloat32(chunks: Float32Array[]): Float32Array {
  let total = 0
  for (const c of chunks) total += c.length
  const out = new Float32Array(total)
  let off = 0
  for (const c of chunks) {
    out.set(c, off)
    off += c.length
  }
  return out
}

function maxAbs(data: Float32Array): number {
  let m = 0
  for (let i = 0; i < data.length; i++) {
    const a = Math.abs(data[i])
    if (a > m) m = a
  }
  return m
}

/** Resample về 16kHz mono bằng OfflineAudioContext. */
async function resampleTo16k(data: Float32Array, srcRate: number): Promise<Float32Array> {
  if (srcRate === 16000) return data
  const src = new AudioBuffer({ length: data.length, sampleRate: srcRate, numberOfChannels: 1 })
  // Bản sao có ArrayBuffer riêng để khớp kiểu Float32Array<ArrayBuffer> của copyToChannel.
  src.copyToChannel(new Float32Array(data), 0)
  const frames = Math.max(1, Math.ceil((data.length * 16000) / srcRate))
  const offline = new OfflineAudioContext(1, frames, 16000)
  const node = offline.createBufferSource()
  node.buffer = src
  node.connect(offline.destination)
  node.start(0)
  const rendered = await offline.startRendering()
  return rendered.getChannelData(0)
}
